import { useEffect, RefObject } from "react";

/**
 * Pulls an element a fraction of the way toward the cursor while hovered.
 *
 * Previously built on GSAP, which meant shipping an entire animation engine —
 * and a second easing vocabulary (`power3.out`, `elastic.out`) that matched
 * nothing else on the page — to move two buttons. A lerp toward a target does
 * the same job, feels like the rest of the site, and lets GSAP leave the bundle
 * altogether.
 *
 * The elastic overshoot on release went with it: springing back past centre
 * drew attention to the mechanism rather than the button.
 */
export function useMagnetic<T extends HTMLElement>(
    ref: RefObject<T | null>,
    strength: number = 0.16
) {
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia("(hover: none)").matches) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        let raf = 0;
        let running = false;
        const target = { x: 0, y: 0 };
        const current = { x: 0, y: 0 };

        const tick = () => {
            current.x += (target.x - current.x) * 0.15;
            current.y += (target.y - current.y) * 0.15;

            // Close enough: snap, stop, and let an idle button hold no rAF loop.
            if (
                Math.abs(target.x - current.x) < 0.1 &&
                Math.abs(target.y - current.y) < 0.1
            ) {
                current.x = target.x;
                current.y = target.y;
                el.style.transform = `translate3d(${current.x}px, ${current.y}px, 0)`;
                running = false;
                return;
            }

            el.style.transform = `translate3d(${current.x}px, ${current.y}px, 0)`;
            raf = requestAnimationFrame(tick);
        };

        const start = () => {
            if (running) return;
            running = true;
            raf = requestAnimationFrame(tick);
        };

        const onMove = (e: MouseEvent) => {
            const rect = el.getBoundingClientRect();
            target.x = (e.clientX - (rect.left + rect.width / 2)) * strength;
            target.y = (e.clientY - (rect.top + rect.height / 2)) * strength;
            start();
        };

        const onLeave = () => {
            target.x = 0;
            target.y = 0;
            start();
        };

        el.addEventListener("mousemove", onMove);
        el.addEventListener("mouseleave", onLeave);

        return () => {
            cancelAnimationFrame(raf);
            el.removeEventListener("mousemove", onMove);
            el.removeEventListener("mouseleave", onLeave);
            el.style.transform = "";
        };
    }, [ref, strength]);
}
